import React, { useMemo } from 'react';
import { get } from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { required } from 'utils/validation/fieldValidationRules';
import TPGrid from 'components/TP-UI/TPGrid';
import TPAlert from 'components/TP-UI/TPAlert';
import TPButton from 'components/TP-UI/TPButton';
import TPErrorMessage from 'components/TP-UI/TPErrorMessage';
import TPCard from 'components/TP-UI/TPCard';
import TPCardContent from 'components/TP-UI/TPCard/components/TPCardContent';
import TPCardTitle from 'components/TP-UI/TPCard/components/TPCardTitle';
import TPCardDescription from 'components/TP-UI/TPCard/components/TPCardDescription';
import CurrencySelect from 'modules/common/CurrencySelect';
import AmountTextField from 'modules/common/AmountTextField';
import Results from '../Results';
import SymbolGroupForm from '../SymbolGroupForm';
import useStyles from './styles';
import { SWAP_TYPES } from '../../constants';

const SwapsCalculatorForm = ({
  loading,
  form,
  symbols,
  currencies,
  baseCurrency,
  contractSize,
  results,
  error = null,
  handleSubmit,
}) => {
  const { t } = useTranslation('tradingCalculators');
  const classes = useStyles();
  const descriptionItems = useMemo(() => {
    const result = [
      [
        {
          dataKey: 'currentConversionPrice',
          label: t(`results.currentConversionPrice`),
        },
        {
          dataKey: 'swapLongCurrencyPair',
          label: t(`results.swapLongCurrencyPair`),
        },
        {
          dataKey: 'swapShortCurrencyPair',
          label: t(`results.swapShortCurrencyPair`),
        },
      ],
      [
        {
          dataKey: 'swapLongCurrency',
          label: t(`results.swapLongCurrency`),
        },
        {
          dataKey: 'swapShortCurrency',
          label: t(`results.swapShortCurrency`),
        },
      ],
      [
        {
          dataKey: 'swapLongConvertedCurrency',
          label: t(`results.swapLongConvertedCurrency`),
        },
        {
          dataKey: 'swapShortConvertedCurrency',
          label: t(`results.swapShortConvertedCurrency`),
        },
      ],
    ];

    if (get(results, '0.type') === SWAP_TYPES.INTEREST) {
      result[0].push({
        dataKey: 'price',
        label: t(`results.price`),
      });
    }

    return result;
  }, [t, results]);

  return (
    <TPGrid container spacing={3}>
      <TPGrid item>
        <TPAlert variant="info">{t('swapsCalculator.description')}</TPAlert>
      </TPGrid>
      <TPGrid item container spacing={2}>
        <TPGrid item xs={12} md={6}>
          <TPCard stretch>
            <TPCardContent>
              <TPCardTitle>{t('calculatorTitle')}</TPCardTitle>
              <TPCardDescription>{t('calculatorDescription')}</TPCardDescription>
              <form noValidate className={classes.form} onSubmit={handleSubmit}>
                <TPGrid container spacing={3} direction="column">
                  {error && (
                    <TPGrid item xs={12}>
                      <TPErrorMessage error={error} size="medium" />
                    </TPGrid>
                  )}
                  <TPGrid item container direction="row" spacing={2}>
                    <TPGrid item xs={12}>
                      <SymbolGroupForm form={form} symbols={symbols} contractSize={contractSize} />
                    </TPGrid>
                    <TPGrid item xs={12} md={6}>
                      <Field
                        component={CurrencySelect}
                        id="baseCurrency"
                        name="baseCurrency"
                        label={t('fields.baseCurrency')}
                        placeholder={t('placeholders.baseCurrency')}
                        options={currencies}
                        required
                        fullWidth
                        validate={[required]}
                      />
                    </TPGrid>
                    <TPGrid item xs={12} md={6}>
                      <Field
                        component={AmountTextField}
                        id="volumeInLots"
                        name="volumeInLots"
                        label={t('fields.volumeInLots')}
                        placeholder={t('placeholders.volumeInLots')}
                        type="number"
                        currency={baseCurrency}
                        required
                        fullWidth
                        validate={[required]}
                      />
                    </TPGrid>
                    <TPGrid item container justifyContent="flex-end">
                      <TPButton
                        primary
                        type="submit"
                        size="large"
                        loading={loading}
                        fullWidth={isMobileOnly}>
                        {t('buttons.calculate')}
                      </TPButton>
                    </TPGrid>
                  </TPGrid>
                </TPGrid>
              </form>
            </TPCardContent>
          </TPCard>
        </TPGrid>
        <TPGrid item xs={12} md={6}>
          <Results loading={loading} results={results} descriptionItems={descriptionItems} />
        </TPGrid>
      </TPGrid>
    </TPGrid>
  );
};

SwapsCalculatorForm.propTypes = {
  form: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  symbols: PropTypes.array.isRequired,
  contractSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currencies: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      flag: PropTypes.string.isRequired,
    }),
  ).isRequired,
  baseCurrency: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default reduxForm()(SwapsCalculatorForm);
